import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalModule,
    MsalRedirectComponent,
    MsalService
} from '@azure/msal-angular';
import {
    BrowserCacheLocation,
    InteractionType,
    PublicClientApplication
} from '@azure/msal-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from '@environments/environment';
import { HttpLoaderFactory } from './services/http-loader.factory';
import { NetworkInterceptor } from "@app/network.interceptor";

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { MainHeaderComponent } from './main-header/main-header.component';
import { MainSidebarComponent } from './main-sidebar/main-sidebar.component';
import { ContentWrapperComponent } from './content-wrapper/content-wrapper.component';
import { ControlSidebarComponent } from './control-sidebar/control-sidebar.component';
import { MainFooterComponent } from './main-footer/main-footer.component';

import { HomeComponent } from "@app/home/home.component";
import { AufgabenComponent } from "@app/aufgaben/aufgaben.component";
import { AufgabeComponent } from "@app/aufgabe/aufgabe.component";
import { FirmenComponent } from '@app/firmen/firmen.component';
import { FirmaComponent } from '@app/firma/firma.component';
import { HistoryComponent } from './history/history.component';
import { StandortdatenAendernComponent } from '@app/standortdaten-aendern/standortdaten-aendern.component';
import { PcAdresseNeuZuordnenComponent } from "@app/pc-adresse-neu-zuordnen/pc-adresse-neu-zuordnen.component";
import { CommentPopupComponent } from "@app/comment-popup/comment-popup.component";

import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { FieldFilterComponent } from './field-filter/field-filter.component';

import { FirmenService } from './services/firmen.service';
import { FirmenstandorteService } from './services/firmenstandorte.service';
import { UserService } from './services/user.service';
import { AufgabenService } from './services/aufgaben.service';

import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyPaginatorIntl as MatPaginatorIntl, MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { DeutschPaginatorIntl } from './deutsch-paginator-intl';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatTableModule } from "@angular/material/table";
import { MatStepperModule } from "@angular/material/stepper";
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatSortModule } from '@angular/material/sort';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';

import { Nl2brPipe } from "@app/nl2br-pipe";


const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

@NgModule({
    declarations: [
        AppComponent,
        MainHeaderComponent,
        MainSidebarComponent,
        ContentWrapperComponent,
        ControlSidebarComponent,
        MainFooterComponent,
        HomeComponent,
        AufgabenComponent,
        AufgabeComponent,
        FirmenComponent,
        FirmaComponent,
        HistoryComponent,
        StandortdatenAendernComponent,
        PcAdresseNeuZuordnenComponent,
        CommentPopupComponent,
        PageNotFoundComponent,
        FieldFilterComponent,
        Nl2brPipe,
    ],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatSidenavModule,
        MatListModule,
        MatToolbarModule,
        MatDialogModule,
        MatGridListModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTableModule,
        MatCardModule,
        MatSlideToggleModule,
        MatRadioModule,
        MatSelectModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatStepperModule,
        MatNativeDateModule,
        MatMenuModule,
        MatSnackBarModule,
        MsalModule.forRoot(
            new PublicClientApplication({
                auth: {
                    clientId: environment.msal.clientId,
                    redirectUri: environment.msal.redirectUrl,
                    authority: environment.msal.authority
                },
                cache: {
                    cacheLocation: BrowserCacheLocation.LocalStorage,
                    storeAuthStateInCookie: isIE
                },
                system: {
                    loggerOptions: {
                        piiLoggingEnabled: false
                    }
                }
            }),
            {
                interactionType: InteractionType.Redirect
            },
            {
                interactionType: InteractionType.Redirect,
                protectedResourceMap: new Map<string, Array<string>>([
                    [environment.apiHost, environment.requiredScopes]
                ])
            }
        ),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        MatDatepickerModule,
        MatSortModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NetworkInterceptor,
            multi: true,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        FirmenService,
        FirmenstandorteService,
        UserService,
        AufgabenService,
        DatePipe,
        {
            provide: MatPaginatorIntl,
            useClass: DeutschPaginatorIntl
        },
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
    /*
    exports: [
        FieldFilterComponent
    ],
    */
    bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
